/* @flow */
import { createAction } from "redux-actions";
import * as ActionTypes from "../../../actionTypes";
import WorkFlowController from "../../../services/workflowController";
import { userAccessTokenSelector, userRoleSelector } from "../../../security";
import { ADMIN_ROLE, MANAGER_ROLE } from "../../../constants";

const workflowController = new WorkFlowController();

type Action = {
  +type: string,
  +payload?: any,
  +error?: boolean,
  +meta?: any
};

export const createInvoicesRequested: Action = createAction(
  ActionTypes.CREATE_INVOICES_REQUESTED
);

export const createInvoicesReceived: Action = createAction(
  ActionTypes.CREATE_INVOICES_RECEIVED
);

export const createInvoicesFailed: Action = createAction(
  ActionTypes.CREATE_INVOICES_FAILED,
  errors => {
    return errors;
  },
  meta => {
    return meta;
  }
);

const createInvoicesAPI = async (data, options) =>
  await workflowController.execute(
    "invoice",
    null,
    "generateInvoices",
    data,
    options
  );
export const createInvoices = (
  locationId,
  dateFrom,
  dateTo,
  applyTaxes
) => async (dispatch, getState) => {
  dispatch(createInvoicesRequested());
  try {
    const userRole = userRoleSelector(getState()); // TODO, can get on server as part req object
    const data = { locationId, dateFrom, dateTo, userRole, applyTaxes };
    const results = await createInvoicesAPI(data, {
      token: userAccessTokenSelector(getState())
    });
    dispatch(
      createInvoicesReceived({
        presentTense: "creating",
        pastTense: "created",
        results
      })
    );
    return true;
  } catch (err) {
    // handles network errors
    console.log(err);
    dispatch(createInvoicesFailed(err.message[0].msg));
    return false;
  }
};

export const fetchAllInvoicesRequested: Action = createAction(
  ActionTypes.FETCH_ALL_INVOICES_REQUESTED
);

export const fetchAllInvoicesReceived: Action = createAction(
  ActionTypes.FETCH_ALL_INVOICES_RECEIVED
);
export const fetchAllInvoicesFailed: Action = createAction(
  ActionTypes.FETCH_ALL_INVOICES_FAILED
);

// DO NOT USE IN UI, ONLY FOR TESTING
// USE fetchInvoices
const fetchRoleInvoicesAPI = async (createdBy, options) =>
  await workflowController.findWhere("invoice", { createdBy }, options);
const fetchAllInvoicesAPI = async options =>
  await workflowController.findWhere("invoice", {}, options);
export const fetchAllInvoices = () => async (dispatch, getState) => {
  dispatch(fetchAllInvoicesRequested());
  try {
    const userRole = userRoleSelector(getState());
    let result;
    if (userRole === ADMIN_ROLE) {
      result = await fetchAllInvoicesAPI({
        token: userAccessTokenSelector(getState())
      });
    } else {
      result = await fetchRoleInvoicesAPI(userRole, {
        token: userAccessTokenSelector(getState())
      });
    }
    dispatch(fetchAllInvoicesReceived(result));
  } catch (err) {
    // handles network errors
    dispatch(fetchAllInvoicesFailed(err.message[0].msg));
  }
};

export const setSelectedInvoice: Action = createAction(
  ActionTypes.SET_SELECTED_INVOICE
);
export const unSetSelectedInvoice: Action = createAction(
  ActionTypes.UNSET_SELECTED_INVOICE
);

export const sendInvoicesRequested: Action = createAction(
  ActionTypes.SEND_INVOICES_REQUESTED
);

export const sendInvoicesReceived: Action = createAction(
  ActionTypes.SEND_INVOICES_RECEIVED
);

export const sendInvoicesFailed: Action = createAction(
  ActionTypes.SEND_INVOICES_FAILED
);

const emailAllUnsentInvoicesAPI = async options =>
  await workflowController.execute("invoice", null, "sendEmails", {}, options);
export const emailAllUnsentInvoices = () => async (dispatch, getState) => {
  dispatch(sendInvoicesRequested());
  try {
    const result = await emailAllUnsentInvoicesAPI({
      token: userAccessTokenSelector(getState())
    });
    dispatch(sendInvoicesReceived());
    if (result.successes && result.successes.length) {
      dispatch(fetchInvoices());
    }
    return result;
  } catch (err) {
    dispatch(sendInvoicesFailed(err.message[0].msg));
    return false;
  }
};

export const addInvoiceItemRequested: Action = createAction(
  ActionTypes.ADD_INVOICE_ITEM_REQUESTED
);

export const addInvoiceItemReceived: Action = createAction(
  ActionTypes.ADD_INVOICE_ITEM_RECEIVED
);

export const addInvoiceItemFailed: Action = createAction(
  ActionTypes.ADD_INVOICE_ITEM_FAILED
);

const addInvoiceItemAPI = async (data, options) =>
  await workflowController.execute("invoice", null, "additem", data, options);

export const addInvoiceItem = (
  invoiceId,
  description,
  price,
  quantity,
  unit,
  applyTaxes
) => async (dispatch, getState) => {
  dispatch(addInvoiceItemRequested());
  try {
    const data = {
      invoiceId,
      description,
      price: parseFloat(price),
      quantity: parseInt(quantity, 10),
      unit,
      applyTaxes
    };
    await addInvoiceItemAPI(data, {
      token: userAccessTokenSelector(getState())
    });
    dispatch(addInvoiceItemReceived());
    dispatch(fetchInvoice(invoiceId));
  } catch (err) {
    dispatch(addInvoiceItemFailed(err.message[0].msg));
  }
};

export const removeInvoiceItemRequested: Action = createAction(
  ActionTypes.REMOVE_INVOICE_ITEM_REQUESTED
);

export const removeInvoiceItemReceived: Action = createAction(
  ActionTypes.REMOVE_INVOICE_ITEM_RECEIVED
);

export const removeInvoiceItemFailed: Action = createAction(
  ActionTypes.REMOVE_INVOICE_ITEM_FAILED
);

const removeInvoiceItemAPI = async (invoiceId, invoiceItemId, options) =>
  await workflowController.execute(
    "invoice",
    null,
    "removeitem",
    { invoiceId, invoiceItemId },
    options
  );

export const removeInvoiceItem = (invoiceId, invoiceItemId) => async (
  dispatch,
  getState
) => {
  dispatch(removeInvoiceItemRequested());
  try {
    await removeInvoiceItemAPI(invoiceId, invoiceItemId, {
      token: userAccessTokenSelector(getState())
    });
    dispatch(removeInvoiceItemReceived());
    dispatch(fetchInvoice(invoiceId));
  } catch (err) {
    dispatch(removeInvoiceItemFailed(err.message[0].msg));
  }
};

export const fetchInvoiceRequested: Action = createAction(
  ActionTypes.FETCH_INVOICE_REQUESTED
);

export const fetchInvoiceReceived: Action = createAction(
  ActionTypes.FETCH_INVOICE_RECEIVED
);

export const fetchInvoiceFailed: Action = createAction(
  ActionTypes.FETCH_INVOICE_FAILED
);

const fetchInvoiceByUserRoleAPI = async (createdBy, invoiceId, options) =>
  await workflowController.findWhere(
    "invoice",
    { createdBy, invoiceId },
    options
  );
const fetchInvoiceAPI = async (invoiceId, options) =>
  await workflowController.findWhere("invoice", { invoiceId }, options);
export const fetchInvoice = invoiceId => async (dispatch, getState) => {
  dispatch(fetchInvoiceRequested());
  try {
    const userRole = userRoleSelector(getState());
    const result =
      userRole === ADMIN_ROLE
        ? await fetchInvoiceAPI(invoiceId, {
            token: userAccessTokenSelector(getState())
          })
        : await fetchInvoiceByUserRoleAPI(userRole, invoiceId, {
            token: userAccessTokenSelector(getState())
          });
    dispatch(fetchInvoiceReceived(result[0]));
  } catch (err) {
    dispatch(fetchInvoiceFailed(err.message[0].msg));
  }
};

export const emailInvoicesRequested: Action = createAction(
  ActionTypes.EMAIL_INVOICES_REQUESTED
);

export const emailInvoicesReceived: Action = createAction(
  ActionTypes.EMAIL_INVOICES_RECEIVED
);

export const emailInvoicesFailed: Action = createAction(
  ActionTypes.EMAIL_INVOICES_FAILED
);

const emailInvoicesAPI = async (invoiceIds, options) =>
  await workflowController.execute(
    "invoice",
    null,
    "email",
    { invoiceIds },
    options
  );
export const emailInvoices = invoiceIds => async (dispatch, getState) => {
  dispatch(emailInvoicesRequested());
  try {
    const results = await emailInvoicesAPI(invoiceIds, {
      token: userAccessTokenSelector(getState())
    });
    dispatch(
      emailInvoicesReceived({
        presentTense: "sending",
        pastTense: "sent",
        results
      })
    );
  } catch (err) {
    // handles network errors
    console.log(err);
    dispatch(emailInvoicesFailed("There was a problem sending invoice."));
  }
};

export const deleteInvoicesRequested: Action = createAction(
  ActionTypes.DELETE_INVOICES_REQUESTED
);

export const deleteInvoicesReceived: Action = createAction(
  ActionTypes.DELETE_INVOICES_RECEIVED
);

export const deleteInvoicesFailed: Action = createAction(
  ActionTypes.DELETE_INVOICES_FAILED
);

const deleteInvoicesAPI = async (invoiceIds, options) =>
  await workflowController.execute(
    "invoice",
    null,
    "delete",
    { invoiceIds },
    options
  );
export const deleteInvoices = invoiceIds => async (dispatch, getState) => {
  dispatch(deleteInvoicesRequested());
  try {
    const results = await deleteInvoicesAPI([...invoiceIds], {
      token: userAccessTokenSelector(getState())
    });
    dispatch(
      deleteInvoicesReceived({
        presentTense: "deleting",
        pastTense: "deleted",
        results
      })
    );
    return true;
  } catch (err) {
    // handles network errors
    console.log(err);
    dispatch(deleteInvoicesFailed("There was a problem deleting invoice."));
    return false;
  }
};

export const printInvoiceRequested: Action = createAction(
  ActionTypes.PRINT_INVOICE_REQUESTED
);

export const printInvoiceReceived: Action = createAction(
  ActionTypes.PRINT_INVOICE_RECEIVED
);

export const printInvoiceFailed: Action = createAction(
  ActionTypes.PRINT_INVOICE_FAILED
);

const printInvoiceAPI = async (invoiceId, options) =>
  await workflowController.printInvoice({ invoiceId }, options);
export const printInvoice = invoiceId => async (dispatch, getState) => {
  dispatch(printInvoiceRequested());
  try {
    await printInvoiceAPI(invoiceId, {
      token: userAccessTokenSelector(getState())
    });
    dispatch(printInvoiceReceived());
  } catch (err) {
    // handles network errors
    console.log(err);
    dispatch(printInvoiceFailed("There was a problem printing the invoice."));
  }
};

export const unsetInvoiceError: Action = createAction(
  ActionTypes.UNSET_INVOICE_ERROR
);

export const editInvoiceItemRequested: Action = createAction(
  ActionTypes.EDIT_INVOICE_ITEM_REQUESTED
);

export const editInvoiceItemReceived: Action = createAction(
  ActionTypes.EDIT_INVOICE_ITEM_RECEIVED
);

export const editInvoiceItemFailed: Action = createAction(
  ActionTypes.EDIT_INVOICE_ITEM_FAILED
);

const editInvoiceItemAPI = async (data, options) =>
  await workflowController.execute("invoice", null, "edititem", data, options);

export const editInvoiceItem = (
  invoiceId,
  invoiceItemId,
  description,
  price,
  quantity,
  unit,
  applyTaxes
) => async (dispatch, getState) => {
  dispatch(editInvoiceItemRequested());
  try {
    const data = {
      invoiceId,
      invoiceItemId,
      description,
      price: parseFloat(price),
      quantity: parseInt(quantity, 10),
      unit,
      applyTaxes
    };
    await editInvoiceItemAPI(data, {
      token: userAccessTokenSelector(getState())
    });
    dispatch(editInvoiceItemReceived());
    dispatch(fetchInvoice(invoiceId));
  } catch (err) {
    dispatch(editInvoiceItemFailed(err.message[0].msg));
  }
};
export const clearInvoicingResults: Action = createAction(
  ActionTypes.CLEAR_INVOICING_RESULTS
);

export const postInvoiceRequested: Action = createAction(
  ActionTypes.POST_INVOICE_REQUESTED
);

export const postInvoiceReceived: Action = createAction(
  ActionTypes.POST_INVOICE_RECEIVED
);

export const postInvoiceFailed: Action = createAction(
  ActionTypes.POST_INVOICE_FAILED
);

const postInvoiceAPI = async (options, invoiceId) =>
  await workflowController.execute(
    "invoice",
    null,
    "post",
    { invoiceId },
    options
  );
export const postInvoice = invoiceId => async (dispatch, getState) => {
  dispatch(postInvoiceRequested());
  try {
    await postInvoiceAPI(
      { token: userAccessTokenSelector(getState()) },
      invoiceId
    );
    dispatch(postInvoiceReceived());
    dispatch(fetchInvoice(invoiceId));
  } catch (err) {
    dispatch(postInvoiceFailed("There was a problem posting invoice."));
  }
};

export const archiveInvoicesRequested: Action = createAction(
  ActionTypes.ARCHIVE_INVOICES_REQUESTED
);

export const archiveInvoicesReceived: Action = createAction(
  ActionTypes.ARCHIVE_INVOICES_RECEIVED
);

export const archiveInvoicesFailed: Action = createAction(
  ActionTypes.ARCHIVE_INVOICES_FAILED
);

const archiveInvoicesAPI = async (options, invoiceIds) =>
  await workflowController.execute(
    "invoice",
    null,
    "archive",
    { invoiceIds },
    options
  );
export const archiveInvoices = invoiceIds => async (dispatch, getState) => {
  dispatch(archiveInvoicesRequested());
  try {
    const results = await archiveInvoicesAPI(
      { token: userAccessTokenSelector(getState()) },
      invoiceIds
    );
    dispatch(
      archiveInvoicesReceived({
        presentTense: "archiving",
        pastTense: "archived",
        results
      })
    );
    return true;
  } catch (err) {
    dispatch(archiveInvoicesFailed("There was a problem archiving invoice."));
    return false;
  }
};

export const fetchDraftInvoicesRequested: Action = createAction(
  ActionTypes.FETCH_DRAFT_INVOICES_REQUESTED
);

export const fetchDraftInvoicesReceived: Action = createAction(
  ActionTypes.FETCH_DRAFT_INVOICES_RECEIVED
);
export const fetchDraftInvoicesFailed: Action = createAction(
  ActionTypes.FETCH_DRAFT_INVOICES_FAILED
);

// NOTE: always only care about drafts created by same role
const fetchDraftInvoicesAPI = async (createdBy, options) =>
  await workflowController.findByFilter(
    "invoice",
    {
      where: { createdBy, datePosted: "", dateEmailed: "", archived: false },
      order: ["invoiceNumber DESC"],
      paging: false
    },
    options
  );
export const fetchDraftInvoices = () => async (dispatch, getState) => {
  dispatch(fetchDraftInvoicesRequested());
  try {
    const userRole = userRoleSelector(getState());
    const result = await fetchDraftInvoicesAPI(userRole, {
      token: userAccessTokenSelector(getState())
    });
    dispatch(fetchDraftInvoicesReceived(result));
  } catch (err) {
    // handles network errors
    dispatch(fetchDraftInvoicesFailed(err.message[0].msg));
  }
};

export const fetchInvoicesRequested: Action = createAction(
  ActionTypes.FETCH_INVOICES_REQUESTED
);

export const fetchInvoicesReceived: Action = createAction(
  ActionTypes.FETCH_INVOICES_RECEIVED
);
export const fetchInvoicesFailed: Action = createAction(
  ActionTypes.FETCH_INVOICES_FAILED
);

const fetchInvoicesAPI = async options =>
  await workflowController.findByFilter(
    "invoice",
    {
      where: { archived: false },
      order: ["invoiceNumber DESC"],
      paging: false
    },
    options
  );
const fetchManagerInvoicesAPI = async options =>
  await workflowController.findByFilter(
    "invoice",
    {
      where: { createdBy: MANAGER_ROLE, archived: false },
      order: ["invoiceNumber DESC"],
      paging: false
    },
    options
  );
export const fetchInvoices = () => async (dispatch, getState) => {
  dispatch(fetchInvoicesRequested());
  try {
    const userRole = userRoleSelector(getState());
    let result;
    if (userRole === MANAGER_ROLE) {
      result = await fetchManagerInvoicesAPI({
        token: userAccessTokenSelector(getState())
      });
    } else {
      result = await fetchInvoicesAPI({
        token: userAccessTokenSelector(getState())
      });
    }
    dispatch(fetchInvoicesReceived(result));
  } catch (err) {
    // handles network errors
    dispatch(fetchInvoicesFailed(err.message[0].msg));
  }
};

export const fetchArchivedInvoicesRequested: Action = createAction(
  ActionTypes.FETCH_ARCHIVED_INVOICES_REQUESTED
);

export const fetchArchivedInvoicesReceived: Action = createAction(
  ActionTypes.FETCH_ARCHIVED_INVOICES_RECEIVED
);
export const fetchArchivedInvoicesFailed: Action = createAction(
  ActionTypes.FETCH_ARCHIVED_INVOICES_FAILED
);

const fetchArchivedAPI = async options =>
  await workflowController.findByFilter(
    "invoice",
    {
      where: { archived: true },
      order: ["invoiceNumber DESC"],
      paging: false
    },
    options
  );
const fetchManagersArchivedInvoicesAPI = async options =>
  await workflowController.findWhere(
    "invoice",
    {
      where: { createdBy: MANAGER_ROLE, archived: true },
      order: ["invoiceNumber DESC"],
      paging: false
    },
    options
  );
export const fetchArchivedInvoices = () => async (dispatch, getState) => {
  dispatch(fetchArchivedInvoicesRequested());
  try {
    const userRole = userRoleSelector(getState());
    let result;
    if (userRole === MANAGER_ROLE) {
      result = await fetchManagersArchivedInvoicesAPI({
        token: userAccessTokenSelector(getState())
      });
    } else {
      result = await fetchArchivedAPI({
        token: userAccessTokenSelector(getState())
      });
    }
    dispatch(fetchArchivedInvoicesReceived(result));
  } catch (err) {
    // handles network errors
    dispatch(fetchArchivedInvoicesFailed(err.message[0].msg));
  }
};



// WEBPACK FOOTER //
// ./src/features/invoice/store/invoiceActions.js